import { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import apiClient from '../../utils/axios';
import AppBanner from '../common/AppBanner';
import './GroupPage.css';
import { STATIC_URL as API_BASE_URL } from '../../config';
import { groupName as resolveGroupName } from '../../config/groups';
import { logEvent } from '../../utils/analytics';

const GroupPage = ({ groupName, groupCode }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pageGroupName, setPageGroupName] = useState(groupName); // 存儲從 API 獲取的組名
  const [pinnedIds, setPinnedIds] = useState([]);

  const storageKey = useMemo(() => `pinnedMembers:${groupCode}`, [groupCode]);

  useEffect(() => {
    logEvent('group_select', { group: groupCode });
  }, [groupCode]);

  useEffect(() => {
    // 載入本地置頂狀態
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setPinnedIds(Array.isArray(parsed) ? parsed : []);
    } catch {
      setPinnedIds([]);
    }
  }, [storageKey]);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        // 直接使用 /members?group= API 路徑
        const response = await apiClient.get(`/members?group=${groupCode}`);

        if (Array.isArray(response.data)) {
          // Sort by term (ascending), then by kana (50音順), then by name
          const sortedMembers = response.data.sort((a, b) => {
            // Primary sort: term (ascending - 小到大)
            if (a.term !== b.term) {
              return a.term - b.term;
            }
            // Secondary sort: by kana (50音順)
            if (a.kana && b.kana) {
              const kanaCompare = a.kana.localeCompare(b.kana, 'ja-JP');
              if (kanaCompare !== 0) {
                return kanaCompare;
              }
            }
            // Tertiary sort: by name (if kana is same or missing)
            return a.name.localeCompare(b.name, 'ja-JP');
          });
          setMembers(sortedMembers);
          
          // 根據 groupCode 設置對應的中文組名（共用團體設定）
          const apiGroupName = resolveGroupName(groupCode) || groupName || '未知團體';

          setPageGroupName(apiGroupName);
          // 設置頁面標題
          document.title = apiGroupName;
        } else {
          setError(true);
          // 設置默認頁面標題
          document.title = groupName;
        }
      } catch (error) {
        setError(true);
        // 設置默認頁面標題
        document.title = groupName;
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [groupCode, groupName]);

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  // 依據置頂狀態重排清單（置頂優先，且各自維持原排序）
  const displayMembers = useMemo(() => {
    if (!Array.isArray(members) || members.length === 0) return [];
    if (pinnedSet.size === 0) return members;
    const pinned = members.filter(m => pinnedSet.has(m.id));
    const unpinned = members.filter(m => !pinnedSet.has(m.id));
    return [...pinned, ...unpinned];
  }, [members, pinnedSet]);

  const togglePin = (memberId) => {
    logEvent('favorite_toggle', {
      member_id: memberId,
      group: groupCode,
      action: pinnedSet.has(memberId) ? 'remove' : 'add',
    });
    setPinnedIds(prev => {
      const set = new Set(prev);
      if (set.has(memberId)) {
        set.delete(memberId);
      } else {
        set.add(memberId);
      }
      const next = Array.from(set);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch (e) {
        console.error('Failed to save pinned members to localStorage:', e);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} className="text-center mt-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">載入中...</span>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-warning">
          無法獲取成員資料，請稍後再試。
        </div>
        {displayMembers.length > 0 && renderMemberList(displayMembers)}
      </Container>
    );
  }

  return (
    <Container>
      <AppBanner />
      {displayMembers.length === 0 ? (
        <div className="alert alert-info">目前沒有成員資料可以顯示。</div>
      ) : (
        renderMemberList(displayMembers)
      )}
    </Container>
  );
  
  // 抽取出渲染成員列表的函數，避免重複代碼
  function renderMemberList(members) {
    return (
      <Row className="g-4 mt-1">
        {members.map(member => {
          const isPinned = pinnedSet.has(member.id);
          return (
            <Col key={member.id} xs={6} md={4} lg={3} className="text-center">
              <div className={`member-card-wrapper ${isPinned ? 'pinned' : ''}`}>
                <div className="card mx-auto shadow-sm h-100">
                  <button
                    type="button"
                    className={`pin-button ${isPinned ? 'pinned' : ''}`}
                    aria-pressed={isPinned}
                    aria-label={isPinned ? '取消置頂' : '置頂'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      togglePin(member.id);
                    }}
                    title={isPinned ? '取消置頂' : '置頂'}
                  >
                    {isPinned ? (
                      <Favorite aria-hidden="true" />
                    ) : (
                      <FavoriteBorder aria-hidden="true" />
                    )}
                  </button>

                <Link
                  to={`/members/${member.id}`}
                  state={isPinned ? { src: 'favorite' } : undefined}
                  className="text-decoration-none d-block">
                  <div className="p-3">
                    <img
                      src={`${API_BASE_URL}${member.avatar}`}
                      className="card-img-top rounded-circle"
                      alt={`${member.name}'s avatar`}
                      onError={(e) => {
                        if (!e.target.dataset.fallbackAttempted) {
                          e.target.dataset.fallbackAttempted = 'true';
                          e.target.src = `${API_BASE_URL}/images/avatar/${pageGroupName}.jpg`;
                        }
                      }}
                    />
                  </div>
                  <div className="card-body">
                    <h5 className="card-title text-center">{member.name}</h5>
                  </div>
                </Link>
              </div>
            </div>
          </Col>
          );
        })}
      </Row>
    );
  }
};

export default GroupPage;
